/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

let cachedBasePath: string | null = null

/**
 * Detects the base path where the SPA is running at RUNTIME.
 * Works for any base path: /, /admin, /my-app, /client/portal, etc.
 *
 * Detection strategy (in order of reliability):
 * 1. Extract from script tag sources (looks for /static/js/ pattern from Create React App)
 * 2. Extract from HTML <base> tag if present
 * 3. Default to / (root)
 *
 * @returns The detected base path (e.g., "/admin", "/", "/my-app")
 */
export function detectBasePath(): string
{
   /////////////////////////////////////////////////////////////////
   // Strategy 0: If the base path was previously determined,     //
   // reuse that result.  this might help prevent issues if React //
   // injects code-splitting chunks after the initial page load   //
   /////////////////////////////////////////////////////////////////
   if (cachedBasePath !== null)
   {
      return cachedBasePath;
   }

   //////////////////////////////////////////////////////////////
   // Strategy 1: Check for HTML <base> tag                    //
   // If the HTML has <base href="/my-app/">, use that.        //
   // The idea being, if someone said "this is the base path", //
   // then trust it - there's no need to figure out anything.  //
   //////////////////////////////////////////////////////////////
   const baseTag = document.querySelector("base");
   if (baseTag && baseTag.getAttribute("href"))
   {
      const href = baseTag.getAttribute("href");
      if (href && href !== "/")
      {
         cachedBasePath = href.endsWith("/") ? href.slice(0, -1) : href;
         return cachedBasePath;
      }
   }


   //////////////////////////////////////////////////////////////////////////////////////////
   // Strategy 2: Try to extract from script tags (most reliable for CRA builds)           //
   // CRA always puts scripts in /static/js/, so we can extract the base path from that    //
   // Example: http://example.com/my-app/static/js/main.abc123.js -> /my-app               //
   // At one time this was thought to be the most reliable approach, but (in a not-yet     //
   // understood way) it was seen to return differing results throughout the SPA lifecycle //
   // if the page was originally loaded from the root path (/).                            //
   //////////////////////////////////////////////////////////////////////////////////////////
   const scripts = document.getElementsByTagName("script");
   for (let i = 0; i < scripts.length; i++)
   {
      const src = scripts[i].src;
      if (src && src.includes("/static/js/"))
      {
         const match = src.match(/^https?:\/\/[^\/]+([\/\w-]*?)\/static\/js\//);
         if (match)
         {
            cachedBasePath = match[1] || "/";
            return cachedBasePath;
         }
      }
   }

   // Default to root
   cachedBasePath = "/";
   return cachedBasePath;
}

/**
 * Logs the detected base path for debugging purposes.
 * Useful for troubleshooting routing and API call issues.
 */
export function logBasePathDetection(): void
{
   const basePath = detectBasePath();
   console.log(`[QQQ] Detected SPA base path: ${basePath}`);
   
   if (basePath !== "/")
   {
      const metadataPath = basePath + "/metaData";
      const dataPath = basePath + "/data/*";
      console.log(`[QQQ] API calls will be directed to: ${metadataPath}, ${dataPath}, etc.`);
      console.log(`[QQQ] React Router will use basename: ${basePath}`);
   }
   else
   {
      console.log("[QQQ] Running at root path - API calls to /metaData, /data/*, etc.");
   }
}

/**
 * Resolves a URL to work with the current SPA base path.
 * 
 * - If the URL is absolute (starts with http:// or https://), returns as-is
 * - If the URL is root-relative (starts with /), prepends the base path
 * - If the URL is already relative or empty, returns as-is
 * 
 * Examples (when basePath is "/admin"):
 * - "/samples-logo2.png" -> "/admin/samples-logo2.png"
 * - "https://example.com/logo.png" -> "https://example.com/logo.png"
 * - "./logo.png" -> "./logo.png"
 * - "" -> ""
 * 
 * @param url The URL to resolve
 * @returns The resolved URL with base path applied if needed
 */
export function resolveAssetUrl(url: string | null | undefined): string
{
   if (!url)
   {
      return "";
   }
   
   // If it's an absolute URL (http/https), return as-is
   if (url.startsWith("http://") || url.startsWith("https://"))
   {
      return url;
   }
   
   // If it's a root-relative path (starts with /), prepend base path
   if (url.startsWith("/"))
   {
      const basePath = detectBasePath();
      if (basePath !== "/")
      {
         return basePath + url;
      }
   }
   
   // Otherwise return as-is (relative paths, data URLs, etc.)
   return url;
}


/***************************************************************************
 * Builds a base-path-relative path (plus query string and hash) suitable for
 * handing to react-router's navigate(), which re-applies the router basename
 * itself.
 *
 * The browser's window.location.pathname INCLUDES the base path, but navigate()
 * prepends the basename - so passing the raw pathname would double the base path
 * (e.g. "/app/orders" -> "/app/app/orders"). This strips the base path so the
 * value round-trips correctly through navigate().
 *
 * Examples (basePath "/app"):
 * - buildReturnToPath("/app/orders", "", "/app")            -> "/orders"
 * - buildReturnToPath("/app/orders", "?tab=1", "/app")      -> "/orders?tab=1"
 * - buildReturnToPath("/app/orders", "", "/app", "#note")   -> "/orders#note"
 * - buildReturnToPath("/app", "", "/app")                   -> "/"
 * Examples (basePath "/"):
 * - buildReturnToPath("/orders", "", "/")                   -> "/orders"
 *
 * @param pathname the raw window.location.pathname (includes base path)
 * @param search the raw window.location.search (includes leading "?", or "")
 * @param basePath the detected base path (e.g. "/app" or "/")
 * @param hash the raw window.location.hash (includes leading "#", or "")
 * @returns a base-relative path (+ search + hash), always starting with "/"
 ***************************************************************************/
export function buildReturnToPath(pathname: string, search: string, basePath: string, hash: string = ""): string
{
   let relative = pathname || "/";

   if (basePath && basePath !== "/")
   {
      if (relative === basePath)
      {
         relative = "/";
      }
      else if (relative.startsWith(basePath + "/"))
      {
         relative = relative.slice(basePath.length);
      }
   }

   if (!relative.startsWith("/"))
   {
      relative = "/" + relative;
   }

   return `${relative}${search || ""}${hash || ""}`;
}


/***************************************************************************
 * Reads the current browser location and returns a base-path-relative path to
 * use as the post-login returnTo. See buildReturnToPath().
 *
 * When includeSearchAndHash is true (the default, used when capturing the
 * originally-requested deep link) the query string and hash are preserved. Pass
 * false when reading the location AT the auth callback - there the search still
 * carries auth0's ?code=...&state=..., which should not be left in the address
 * bar - to get a bare pathname.
 *
 * @param includeSearchAndHash whether to append window.location.search and hash
 * @returns the current location as a base-relative path
 ***************************************************************************/
export function getReturnToPath(includeSearchAndHash: boolean = true): string
{
   const search = includeSearchAndHash ? window.location.search : "";
   const hash = includeSearchAndHash ? window.location.hash : "";
   return buildReturnToPath(window.location.pathname, search, detectBasePath(), hash);
}


/**
 * Clears the cached base path, forcing detection to run again next time it's needed.
 *
 * Not expected to be needed in normal use cases, but can be useful for testing.
 */
export function clearCachedBasePath()
{
   cachedBasePath = null;
}

